import { type FC, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useTranslation,
  useQueryWithSSRErrors,
  usePlatform,
} from "@/shared/hooks";
import { StreamContentQuery } from "@/entities/contents";
import type { StreamContentQueryType } from "@/types/graphql/site.generated";
import { ErrorScreen } from "@/shared/components";
import { ContentPlayer } from "@/shared/components/ContentPlayer";
import { FixWithAssistantButton } from "@/features/assistant";
import { CloseRouteDialogButton } from "@/shared/components/RouteDialog";
import { PromoButtonWidget } from "@/shared/components/PromoButtonWidget";
import {
  ContentLayout,
  type ContentLayoutTab,
} from "@/widgets/VideoScreen/ContentLayout";
import { ContentBody } from "@/widgets/VideoScreen/ContentBody";
import { FeaturedVideos } from "@/widgets/VideoScreen/FeaturedVideos";
import { VideoScreen } from "@/widgets/VideoScreen";
import { ContentChat } from "@/widgets/ContentChat";
import { useContentStreamStatusChangedSubscription } from "./useContentStreamStatusChangedSubscription";
import { StreamCountdown } from "./StreamCountdown";
import { StreamReminderButton } from "./StreamReminderButton";
import {
  getPaymentButtons,
  getPaymentButtonText,
} from "@/shared/components/ContentPlayer/PaymentScreen";
import { useContentPurchase } from "@/widgets/ContentPurchase";
import {
  ContentAccessErrorTypeType,
  PlatformFeatureType,
} from "@/types/graphql/site.generated";
import { MetaHead } from "@/shared/components/MetaHead";
import {
  embedPathOf,
  metaImageOf,
} from "@/shared/components/MetaHead/metaTags";
import {
  StructuredData,
  buildBreadcrumbs,
  buildEvent,
  buildVideoObject,
  eventId,
} from "@/shared/components/StructuredData";
// Прямой импорт, не из барреля хуков: тесты экрана подменяют баррель
// целиком, а хук хоста должен работать и там (без хоста разметки нет).
import { useViewerHost } from "@/shared/hooks/use-viewer-host";
import { getMinPrice } from "@/shared/utils/getMinPrice";
import { LiveReactionsOverlay } from "@/features/liveReactions";
import { platformHasFeature } from "@/shared/utils/platformFeatures";
import { PollsOverlay } from "@/features/polls";

export const StreamScreen: FC<{ id?: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { platform } = usePlatform();
  // Мобильный слот под плеером (спека live-reactions-and-polls): туда
  // уезжают панель реакций и карточка опроса на узких экранах, оверлеи
  // получают сам элемент через PlayerOverlaySlot. Это первый useState
  // экрана, на него рассчитывает StreamScreen.test; хуки со своим
  // состоянием (useViewerHost) идут после.
  const [mobileSlot, setMobileSlot] = useState<HTMLDivElement | null>(null);
  const host = useViewerHost();
  const { data, loading, error, refetch } =
    useQueryWithSSRErrors<StreamContentQueryType>(StreamContentQuery, {
      variables: { id },
      skip: !id,
    });
  // Покупка прямо на экране эфира (счётчик или экран оплаты плеера): после
  // успешной оплаты перезапрашиваем контент, чтобы доступ обновился.
  const purchase = useContentPurchase(() => void refetch());
  const content = data?.content;

  // Живое переключение PENDING → STARTED → FINISHED без перезагрузки —
  // WS-транспорт (Task 27); при обрыве сокета graphql-ws сам переподключится,
  // отдельный polling-фолбэк на стороне сайта не нужен (backend уже
  // подстраховывает статус вебхуком+polling, см. design spec §5).
  useContentStreamStatusChangedSubscription(id);

  // Как и в VideoScreen: platform (а с ним и showFeatured) доступен ещё до
  // ответа content-запроса, потому что usePlatform читается независимо —
  // это позволяет зарезервировать колонку сайдбара уже в loading-ветке и
  // избежать «прыжка» вёрстки, когда StreamScreen монтируется без
  // SSR-данных (dynamic ssr:false в _app.tsx, см. фикс-раунд ниже).
  const showFeatured = platform?.isFeaturedVideosEnabled !== false;

  // Реакции: фичефлаг админа и тумблер партнёра (сервер отдаёт итоговое
  // isLiveReactionsEnabled, флаг проверяем явно по спеке).
  const reactionsEnabled =
    platformHasFeature(platform, PlatformFeatureType.LiveReactionsType) &&
    Boolean(platform?.isLiveReactionsEnabled);
  // Опросы: только фичефлаг админа PLAYER_POLLS, партнёрского тумблера нет.
  const pollsEnabled = platformHasFeature(
    platform,
    PlatformFeatureType.PlayerPollsType,
  );

  // JS-условие вместо CSS display:{xs,md} — CSS-скрытие не мешает React
  // монтировать компонент и его хуки/подписки (useChatHeartbeat,
  // useChatSoundNotifications, GraphQL-подписки), поэтому desktop- и
  // mobile-инстансы ContentChat были бы одновременно живыми, дублируя
  // heartbeat/leave-циклы и звуковые уведомления (см. фикс-раунд ниже).
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  if ((loading && !data) || !id) {
    return (
      <ContentLayout
        sidebar={
          showFeatured && id ? (
            <FeaturedVideos contentId={id} variant="sidebar" />
          ) : null
        }
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
          <CloseRouteDialogButton />
        </Box>
        <Box sx={{ pt: { xs: 1.5, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
          <Skeleton variant="text" sx={{ fontSize: "2rem", maxWidth: 480 }} />
          <Skeleton
            variant="rounded"
            sx={{ mt: 2, borderRadius: 50, height: 42, maxWidth: 360 }}
          />
        </Box>
      </ContentLayout>
    );
  }

  if (error || !content) {
    // У ErrorScreen нет пропа overlay (в отличие от ContentPlayer/
    // AccessErrorScreen) — оборачиваем сами тем же паттерном, что и в
    // ContentPlayer.tsx (Box position:relative + оверлей соседним элементом),
    // чтобы кнопка закрытия диалога была доступна и на экране ошибки.
    return (
      <Box sx={{ position: "relative" }}>
        <ErrorScreen error={error} />
        <CloseRouteDialogButton />
      </Box>
    );
  }

  const stream = content.stream;
  // Комната переехала со стрима на сам контент: один и тот же чат теперь
  // и под видео, и под стримом (спека §1).
  const chatRoom = content.isChatEnabled ? content.chatRoom : null;

  // FINISHED + есть запись — страница стрима полностью подменяется на
  // обычную VOD-страницу записи (см. design spec §4): паритет с видео
  // (ContentBody/сайдбар/og-теги) получаем бесплатно, без дублирования.
  if (
    stream?.status === "FINISHED" &&
    stream.hasRecord &&
    stream.recordContentId
  ) {
    return <VideoScreen id={stream.recordContentId} />;
  }

  // Паритет с VideoScreen: сайдбар рекомендаций (видео+стримы, см. Task 1)
  // и og:meta теги показываются и до, и во время эфира — только пока
  // запись ещё обрабатывается (FINISHED без записи) экран остаётся
  // минимальным транзитным сообщением, как и раньше.
  const tabs: ContentLayoutTab[] = [
    // Чат идёт первой вкладкой: на эфире он важнее рекомендаций, и вкладка
    // по умолчанию в ContentLayout это первая в списке.
    ...(chatRoom && isDesktop
      ? [
          {
            value: "CHAT",
            label: t("stream.chat.tabLabel"),
            content: (
              <ContentChat
                chatRoom={{
                  id: String(chatRoom.id),
                  viewerIsModerator: chatRoom.viewerIsModerator,
                  viewerBan: chatRoom.viewerBan ?? null,
                  onlineCount: chatRoom.onlineCount,
                  onlineMembers: chatRoom.onlineMembers ?? [],
                }}
                mode="stream"
                variant="sidebar"
              />
            ),
          },
        ]
      : []),
    ...(showFeatured
      ? [
          {
            value: "FEATURED_VIDEO",
            label: t("video.similar.title"),
            content: (
              <FeaturedVideos contentId={content.id} variant="sidebar" />
            ),
          },
        ]
      : []),
  ];
  const ogImage = metaImageOf(content.preview);
  const thumbnailUrl =
    ogImage?.url ?? (platform?.logo?.url ? String(platform.logo.url) : null);

  // JSON-LD эфира: Event (расширенные результаты Google) пока эфир
  // ожидается или идёт, VideoObject с BroadcastEvent всегда; завершённый
  // без записи Event не получает. Цена в offers это минимальная из
  // продуктов платного эфира, бесплатный эфир помечен isAccessibleForFree.
  const isUpcomingOrLive =
    stream?.status === "PENDING" || stream?.status === "STARTED";
  const minPrice = content.premium
    ? getMinPrice(content.products.nodes.flatMap((product) => product.prices))
    : undefined;
  const streamEvent =
    platform && isUpcomingOrLive
      ? buildEvent(host, platform, {
          path: content.path,
          name: content.title,
          description: content.description,
          image: thumbnailUrl,
          startDate: stream?.startAt,
          endDate: stream?.finishAt,
          price: minPrice
            ? { value: minPrice.value, currency: minPrice.currency }
            : null,
          validFrom: content.publishAt,
        })
      : null;
  const structuredData = platform ? (
    <StructuredData
      nodes={[
        streamEvent,
        buildVideoObject(host, platform, content, thumbnailUrl, {
          linkedEventId: streamEvent ? eventId(host, content.path) : null,
        }),
        buildBreadcrumbs(host, platform, [
          { name: content.title, path: content.path },
        ]),
      ]}
    />
  ) : null;
  const metaHead = (
    <MetaHead
      title={content.title}
      description={content.description ?? content.title}
      image={ogImage}
      path={content.path}
      url={content.url ? String(content.url) : null}
      type="video.other"
      tags={content.tags}
      videoReleaseDate={content.publishAt}
      embedPath={embedPathOf(content.id)}
    />
  );

  // Событие отменено партнёром (отмена PPV): вместо плеера и счётчика
  // экран отмены. Статус приходит подпиской, экран переключается сам.
  if (stream?.status === "CANCELLED") {
    return (
      <>
        {metaHead}
        <ContentLayout tabs={tabs}>
          <Box
            sx={(theme) => ({
              position: "relative",
              width: "100%",
              aspectRatio: { xs: "auto", sm: "16 / 9" },
              py: { xs: 4, sm: 0 },
              backgroundColor: theme.palette.background.default,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            })}
          >
            <Stack
              spacing={1}
              alignItems="center"
              sx={{ px: 2, textAlign: "center", color: "text.primary" }}
            >
              <Typography variant="h5" component="p">
                {t("stream.cancelled.title")}
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                {t("stream.cancelled.text")}
              </Typography>
            </Stack>
            <CloseRouteDialogButton />
          </Box>
          <ContentBody content={content} />
        </ContentLayout>
      </>
    );
  }

  const needsPurchase =
    content.siteAccess?.error ===
    ContentAccessErrorTypeType.ContentIsNotAvailableForFreeViewingType;
  const purchaseButtons = needsPurchase
    ? getPaymentButtons(content.products.nodes)
    : [];

  if (!stream || stream.status === "PENDING") {
    const coverUrl =
      content.preview?.thumbnails?.find(
        (t) => t.format === "MAXRES" || t.format === "HIGH",
      )?.url ??
      content.preview?.url ??
      null;

    return (
      <>
        {metaHead}
        {structuredData}
        <ContentLayout tabs={tabs}>
          {/* className="paywall": JSON-LD платного эфира ссылается на
              .paywall (VideoObject.hasPart, см. StructuredData/builders.ts);
              до старта плеера нет, поэтому класс стоит на области счётчика,
              как в ContentPlayer. */}
          <Box
            className={content.premium ? "paywall" : undefined}
            sx={(theme) => ({
              position: "relative",
              width: "100%",
              // На телефоне область 16:9 слишком низкая для счётчика с
              // кнопками: высота по содержимому, пропорция только с sm.
              aspectRatio: { xs: "auto", sm: "16 / 9" },
              py: { xs: 4, sm: 0 },
              overflow: "hidden",
              backgroundColor: theme.palette.background.default,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            })}
          >
            {coverUrl && (
              <Box
                component="img"
                src={String(coverUrl)}
                alt=""
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: 0.35,
                  filter: "blur(2px)",
                  transform: "scale(1.03)",
                }}
              />
            )}
            <Box
              sx={(theme) => ({
                position: "absolute",
                inset: 0,
                background: `radial-gradient(ellipse at center, ${alpha(theme.palette.background.default, 0.2)} 0%, ${alpha(theme.palette.background.default, 0.85)} 100%)`,
              })}
            />
            <Stack
              alignItems="center"
              spacing={2}
              sx={{
                position: "relative",
                width: "100%",
                px: 2,
                color: "text.primary",
              }}
            >
              {stream?.startAt ? (
                <StreamCountdown startAt={stream.startAt} />
              ) : null}
              {/* Платный эфир без доступа: кнопка покупки прямо у счётчика
                  (референс показывал PaymentScreen с ценой вместо
                  плеера), ведёт на страницу продукта. */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 1.5 }}
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiButton-root": {
                    fontSize: { xs: "0.75rem", sm: "0.9375rem" },
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1, sm: 2 },
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: 360,
                  },
                }}
              >
                {purchaseButtons.map(({ product, minPrice, products }) => (
                  <Button
                    key={product.id}
                    variant="contained"
                    size="large"
                    onClick={() => purchase.openProducts(products)}
                  >
                    {getPaymentButtonText(
                      t,
                      product,
                      minPrice,
                      products.length > 1,
                    )}
                  </Button>
                ))}
                <StreamReminderButton
                  contentId={content.id}
                  viewerSubscription={content.viewerSubscription ?? null}
                />
              </Stack>
            </Stack>
            <CloseRouteDialogButton />
          </Box>
          <ContentBody content={content} />
          {showFeatured && (
            <Box sx={{ display: { xs: "block", md: "none" }, pt: 1 }}>
              <FeaturedVideos contentId={content.id} variant="mobile" />
            </Box>
          )}
          {chatRoom && !isDesktop && (
            <Box sx={{ pt: 1 }}>
              <ContentChat
                chatRoom={{
                  id: String(chatRoom.id),
                  viewerIsModerator: chatRoom.viewerIsModerator,
                  viewerBan: chatRoom.viewerBan ?? null,
                  onlineCount: chatRoom.onlineCount,
                  onlineMembers: chatRoom.onlineMembers ?? [],
                }}
                mode="stream"
                variant="mobile"
              />
            </Box>
          )}
        </ContentLayout>
        {purchase.dialogs}
      </>
    );
  }

  if (stream.status === "FINISHED") {
    // Запись ещё обрабатывается (hasRecord=false). Раньше здесь был
    // транзитный чёрный блок без заголовка и описания, но страница должна
    // выглядеть как обычно: та же раскладка, что и до старта, со статусом
    // внутри области плеера на затемнённой обложке (Борис, 2026-10-03).
    const coverUrl =
      content.preview?.thumbnails?.find(
        (t) => t.format === "MAXRES" || t.format === "HIGH",
      )?.url ??
      content.preview?.url ??
      null;

    return (
      <>
        {metaHead}
        {structuredData}
        <ContentLayout tabs={tabs}>
          <Box
            sx={(theme) => ({
              position: "relative",
              width: "100%",
              aspectRatio: { xs: "auto", sm: "16 / 9" },
              py: { xs: 4, sm: 0 },
              overflow: "hidden",
              backgroundColor: theme.palette.background.default,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            })}
          >
            {coverUrl && (
              <Box
                component="img"
                src={String(coverUrl)}
                alt=""
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  opacity: 0.35,
                  filter: "blur(2px)",
                  transform: "scale(1.03)",
                }}
              />
            )}
            <Box
              sx={(theme) => ({
                position: "absolute",
                inset: 0,
                background: `radial-gradient(ellipse at center, ${alpha(theme.palette.background.default, 0.2)} 0%, ${alpha(theme.palette.background.default, 0.85)} 100%)`,
              })}
            />
            <Box
              sx={{
                position: "relative",
                textAlign: "center",
                px: 2,
                color: "text.primary",
              }}
            >
              <Typography>{t("stream.finished.processing")}</Typography>
            </Box>
            <CloseRouteDialogButton />
          </Box>
          <ContentBody content={content} />
          {showFeatured && (
            <Box sx={{ display: { xs: "block", md: "none" }, pt: 1 }}>
              <FeaturedVideos contentId={content.id} variant="mobile" />
            </Box>
          )}
          {chatRoom && !isDesktop && (
            <Box sx={{ pt: 1 }}>
              <ContentChat
                chatRoom={{
                  id: String(chatRoom.id),
                  viewerIsModerator: chatRoom.viewerIsModerator,
                  viewerBan: chatRoom.viewerBan ?? null,
                  onlineCount: chatRoom.onlineCount,
                  onlineMembers: chatRoom.onlineMembers ?? [],
                }}
                mode="stream"
                variant="mobile"
              />
            </Box>
          )}
        </ContentLayout>
      </>
    );
  }

  // STARTED — reuse the VOD player unchanged (see design note above): the
  // selection in StreamContentQuery is a structural superset of
  // ContentQueryType["content"] (same fields minus contentTimecodes, which is
  // optional there), so `content` is assignable to ContentPlayer's and
  // ContentBody's prop as-is.
  return (
    <>
      {metaHead}
      {structuredData}
      <ContentLayout tabs={tabs}>
        <ContentPlayer
          key={content.id}
          content={content}
          source="SITE"
          autoPlay
          isLive={stream.status === "STARTED"}
          onProductClick={purchase.openProducts}
          renderAssistantAction={({ errorCode, contentId, playerError }) => (
            <FixWithAssistantButton
              errorCode={errorCode}
              contentId={contentId}
              playerError={playerError}
            />
          )}
          overlay={({ controlsVisible, currentTime, isCasting }) => (
            <>
              <CloseRouteDialogButton />
              <PromoButtonWidget
                contentId={content.id}
                widget={content.widget}
                visible={controlsVisible}
              />
              {reactionsEnabled && !content.siteAccess.error && (
                <LiveReactionsOverlay
                  contentId={content.id}
                  phrases={platform?.liveReactionPhrases ?? []}
                  emojis={platform?.liveReactionEmojis ?? []}
                  controlsVisible={controlsVisible}
                  isCasting={isCasting}
                />
              )}
              {pollsEnabled && !content.siteAccess.error && (
                <PollsOverlay
                  contentId={content.id}
                  hostType="STREAM"
                  initialPolls={content.polls}
                  controlsVisible={controlsVisible}
                  currentTime={currentTime}
                  isCasting={isCasting}
                  mobileSlot={mobileSlot}
                />
              )}
            </>
          )}
        />
        <Box
          ref={setMobileSlot}
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            gap: 1,
            px: 1.5,
            pt: 1,
            "&:empty": { display: "none" },
          }}
        />
        <ContentBody
          content={content}
          live={{ viewersOnline: stream.viewersOnline }}
        />
        {showFeatured && (
          <Box sx={{ display: { xs: "block", md: "none" }, pt: 1 }}>
            <FeaturedVideos contentId={content.id} variant="mobile" />
          </Box>
        )}
        {chatRoom && !isDesktop && (
          <Box sx={{ pt: 1 }}>
            <ContentChat
              chatRoom={{
                id: String(chatRoom.id),
                viewerIsModerator: chatRoom.viewerIsModerator,
                viewerBan: chatRoom.viewerBan ?? null,
                onlineCount: chatRoom.onlineCount,
                onlineMembers: chatRoom.onlineMembers ?? [],
              }}
              mode="stream"
              variant="mobile"
            />
          </Box>
        )}
      </ContentLayout>
      {purchase.dialogs}
    </>
  );
};
